import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown, Globe, Briefcase, GraduationCap, Users, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContactChannel } from '@/hooks/useContactChannel';

const navItems = [
  { 
    label: 'Home', 
    path: '/' 
  },
  { 
    label: 'About', 
    path: '/about' 
  },
  {
    label: 'Services',
    path: '#',
    submenu: [
      { label: 'International Manpower Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia', icon: Globe },
      { label: 'Russia Recruitment Agency', path: '/russia-recruitment-agency-india', icon: Globe },
      { label: 'Poland Recruitment Agency', path: '/poland-recruitment-agency-india', icon: Globe },
      { label: 'Eastern Europe Recruitment Hub', path: '/eastern-europe-recruitment-india', icon: Globe },
      { label: 'Work Visa Recruitment', path: '/countries/canada', icon: Briefcase },
      { label: 'Skilled Migration', path: '/countries/australia', icon: Users },
      { label: 'Business/Investor Placement', path: '/business/usa/eb-5-visa', icon: Building },
      { label: 'Study Abroad Placement', path: '/study-abroad/', icon: GraduationCap },
    ]
  },
  {
    label: 'Countries',
    path: '#',
    submenu: [
      { 
        label: 'Canada', 
        path: '/countries/canada',
        submenu: [
          { label: 'Canada Hub (Compare Pathways)', path: '/countries/canada' },
          { label: 'Canada PR Visa', path: '/countries/canada/canada-pr-visa' },
          { label: 'Canada Express Entry', path: '/cms/canada-express-entry' },
        ]
      },
      { 
        label: 'Australia', 
        path: '/countries/australia',
        submenu: [
          { label: 'Australia Hub (Compare Pathways)', path: '/countries/australia' },
          { label: 'Subclass 189 — Skilled Independent', path: '/skilled/australia/australia-skilled-independent-visa-189' },
          { label: 'Subclass 190 — State Nominated', path: '/skilled/australia/australia-skilled-nominated-visa-190' },
          { label: 'Subclass 482 — Employer-Sponsored', path: '/skilled/australia/australia-subclass-482-visa' },
          { label: 'Subclass 491 — Skilled Regional', path: '/skilled/australia/nominated-or-sponsored-visa-489' },
        ]
      },
      { label: 'Saudi Arabia', path: '/saudi-arabia' },
      { label: 'USA', path: '/business/usa/eb-5-visa' },
      { label: 'UK', path: '/skilled/uk/uk-skilled-worker-visa' },
      { label: 'Germany', path: '/germany-opportunity-card' },
      { label: 'UAE', path: '/skilled/uae/uae-golden-visa' },
      { label: 'New Zealand', path: '/skilled/new-zealand/new-zealand-pr-visa' },
    ]
  },
  { 
    label: 'Points Calculator', 
    path: '/calculators' 
  },
  {
    // Jobs — top-level overseas-jobs silo. Replaces "Success Stories" in
    // header nav (Success Stories lives in the footer Corporate links).
    label: 'Jobs',
    path: '/jobs',
    submenu: [
      { label: 'Overseas Jobs Hub (All Sectors)', path: '/jobs', icon: Briefcase },
      { label: 'IT & Tech', path: '/jobs/tech-jobs-saudi-arabia' },
      { label: 'Finance & Corporate', path: '/jobs/finance-jobs-saudi-arabia' },
      { label: 'Engineering & Construction', path: '/jobs/engineering-jobs-saudi-arabia' },
      { label: 'Healthcare & Medical', path: '/jobs/healthcare-jobs-saudi-arabia' },
      { label: 'Education & Teaching', path: '/jobs/education-jobs-saudi-arabia' },
    ],
  },
  { 
    label: 'Blogs', 
    path: '/blogs' 
  },
  { 
    label: 'Contact', 
    path: '/contact' 
  },
  {
    label: 'Study Abroad',
    path: '/study-abroad/',
    isExternalVertical: true,
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();
  const channel = useContactChannel();
  
  // Check if on homepage
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveSubmenu(null);
  }, [location]);

  const openWhatsApp = () => {
    window.open(channel.whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // Determine if we should show dark header (on homepage)
  const showDarkHeader = isHomePage && !isScrolled;

  return (
    <>
      {/* Government Approval Top Bar - Animated Marquee + Study Abroad Bridge */}
      <div className={`text-xs md:text-sm transition-colors duration-300 ${
        showDarkHeader 
          ? 'bg-slate-900/80 backdrop-blur-sm text-white border-b border-white/5' 
          : 'bg-slate-900 text-white'
      }`}>
        <div className="flex items-stretch">
          {/* Marquee (takes remaining space) */}
          <div className="flex-1 min-w-0 overflow-hidden py-2">
            <div className="animate-marquee whitespace-nowrap flex items-center">
              <span className="mx-8 flex items-center gap-2">
                <span className="font-medium tracking-wide">
                  Best Overseas Recruitment Agency in Delhi · MEA Approved · 10,000+ Placements
                </span>
                <span className="text-[#C5A028] font-medium">(RC: B-0762/DEL/PER/1000+/5/9148/2015)</span>
              </span>
              <span className="mx-8 flex items-center gap-2">
                <span className="font-medium tracking-wide">
                  Best Overseas Recruitment Agency in Delhi · MEA Approved · 10,000+ Placements
                </span>
                <span className="text-[#C5A028] font-medium">(RC: B-0762/DEL/PER/1000+/5/9148/2015)</span>
              </span>
              <span className="mx-8 flex items-center gap-2">
                <span className="font-medium tracking-wide">
                  Best Overseas Recruitment Agency in Delhi · MEA Approved · 10,000+ Placements
                </span>
                <span className="text-[#C5A028] font-medium">(RC: B-0762/DEL/PER/1000+/5/9148/2015)</span>
              </span>
            </div>
          </div>

          {/* Cross-Discovery: Study Abroad pill (desktop only, fixed right slot) */}
          <Link
            to="/study-abroad/"
            className="hidden md:inline-flex items-center gap-1.5 shrink-0 self-center mr-3 ml-2 bg-[#C5A028]/20 hover:bg-[#C5A028]/35 text-[#FACC15] border border-[#C5A028]/50 hover:border-[#C5A028]/80 rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-all whitespace-nowrap"
            data-testid="utility-bar-study-abroad-pill"
          >
            <GraduationCap className="w-3.5 h-3.5" />
            Looking to Study Abroad? Click Here
            <span className="text-[10px] opacity-80">↗</span>
          </Link>
        </div>
      </div>

      {/* Main Header */}
      {(() => {
        // Resolve header background classes — readable replacement for nested ternary.
        let headerBgClasses = 'bg-slate-950 py-4';
        if (showDarkHeader) {
          headerBgClasses = 'bg-transparent';
        } else if (isScrolled) {
          headerBgClasses = 'bg-slate-950/95 backdrop-blur-xl shadow-lg shadow-black/10 py-3 border-b border-white/5';
        }
        return (
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${headerBgClasses}`}
        data-testid="main-header"
      >
        <div className="container max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/zafco-logo.png" 
                alt="ZAFCO - International Recruitment Agency" 
                className="h-12 w-auto"
                width="180"
                height="48"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <div 
                  key={item.label} 
                  className="relative group"
                  onMouseEnter={() => item.submenu && setActiveSubmenu(item.label)}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  {item.submenu ? (
                    <button 
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                        showDarkHeader 
                          ? 'text-slate-300 hover:text-white' 
                          : 'text-slate-400 hover:text-white'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  ) : (
                    (() => {
                      // Resolve link className via lookup-first pattern instead of nested ternary
                      let linkClass;
                      if (item.isExternalVertical) {
                        linkClass = 'text-[#C5A028] hover:text-[#EAB308]';
                      } else if (location.pathname === item.path) {
                        linkClass = 'text-[#C5A028]';
                      } else if (showDarkHeader) {
                        linkClass = 'text-slate-300 hover:text-white';
                      } else {
                        linkClass = 'text-slate-400 hover:text-white';
                      }
                      return (
                        <Link
                          to={item.path}
                          className={`px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 ${linkClass}`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.isExternalVertical && <GraduationCap className="w-3.5 h-3.5" />}
                          {item.label}
                          {item.isExternalVertical && <span className="text-[10px] opacity-70">↗</span>}
                        </Link>
                      );
                    })()
                  )}
                  
                  {/* Submenu */}
                  {item.submenu && (
                    <AnimatePresence>
                      {activeSubmenu === item.label && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                        >
                          {item.submenu.map((subItem) => (
                            <div key={subItem.label} className="relative group/nested">
                              {subItem.submenu ? (
                                <>
                                  <Link 
                                    to={subItem.path}
                                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                                    data-testid={`subnav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <span className="flex items-center gap-3">
                                      {subItem.icon && <subItem.icon className="w-4 h-4 text-[#C5A028]" />}
                                      {subItem.label}
                                    </span>
                                    <ChevronDown className="w-3 h-3 -rotate-90" />
                                  </Link>
                                  {/* Nested submenu */}
                                  <div className="absolute left-full top-0 ml-1 w-64 bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden opacity-0 invisible group-hover/nested:opacity-100 group-hover/nested:visible transition-all duration-200">
                                    {subItem.submenu.map((nestedItem) => (
                                      <Link 
                                        key={nestedItem.label}
                                        to={nestedItem.path}
                                        className="flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                                        data-testid={`subnav-nested-${nestedItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                      >
                                        {nestedItem.label}
                                      </Link>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <Link 
                                  to={subItem.path}
                                  className="flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                                  data-testid={`subnav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {subItem.icon && <subItem.icon className="w-4 h-4 text-[#C5A028]" />}
                                  {subItem.label}
                                </Link>
                              )}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="flex items-center gap-4">
              <Button 
                onClick={openWhatsApp}
                className="hidden md:flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold rounded-full px-5 py-2 text-sm transition-all hover:scale-105"
                data-testid="header-cta"
              >
                <Phone className="w-4 h-4" />
                Contact Us
              </Button>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-white"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>
        );
      })()}

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden fixed inset-x-0 top-[105px] bg-slate-950 border-b border-white/10 z-40 overflow-hidden"
          >
            <nav className="container mx-auto px-6 py-6 space-y-2">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.submenu ? (
                    <div>
                      <button 
                        onClick={() => setActiveSubmenu(activeSubmenu === item.label ? null : item.label)}
                        className="w-full flex items-center justify-between py-3 text-white font-medium border-b border-white/5"
                      >
                        {item.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${activeSubmenu === item.label ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeSubmenu === item.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="pl-4 space-y-2 pt-2 overflow-hidden"
                          >
                            {item.submenu.map((subItem) => (
                              <Link 
                                key={subItem.label}
                                to={subItem.path}
                                className="flex items-center gap-2 py-2 text-slate-400 hover:text-white"
                              >
                                {subItem.icon && <subItem.icon className="w-4 h-4 text-[#C5A028]" />}
                                {subItem.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link 
                      to={item.path}
                      className={`block py-3 font-medium border-b border-white/5 inline-flex items-center gap-2 ${
                        item.isExternalVertical
                          ? 'text-[#C5A028]'
                          : location.pathname === item.path ? 'text-[#C5A028]' : 'text-white'
                      }`}
                    >
                      {item.isExternalVertical && <GraduationCap className="w-4 h-4" />}
                      {item.label}
                      {item.isExternalVertical && <span className="text-[10px] opacity-70">↗</span>}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA */}
              <Button 
                onClick={openWhatsApp}
                className="w-full mt-6 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold rounded-full py-6"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Us Now
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
